import JumboCardQuick from '@jumbo/components/JumboCardQuick';
import {
    Autocomplete,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from '@mui/material';
import { deleteObjectApi } from 'api/general';
import { deletePeriodicReportFileApi, postUploadPeriodicReportFileApi } from 'api/meeting';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import DzPreviews from '../DzPreviews';
import PeriodicReportListTable from '../Table/PeriodicReportListTable';
import { PERIODIC_REPORT } from 'app/utils/constants/customConstant';
import { swal_error } from 'app/utils/appHelpers';
import { useTemplateFontSettings } from 'styles/templateFontSettings';

function PeriodicReportFileCard({ fileList, setFileList, selectedUnit, addAfterTable, updateAfterTable, existingReports }) {
    const isTableRow = !!selectedUnit;

    const [localUnit, setLocalUnit] = useState(null);
    const effectiveUnit = isTableRow ? selectedUnit : localUnit;
    const effectiveUnitId = effectiveUnit ? (isTableRow ? effectiveUnit.unitId : effectiveUnit.id) : null;

    const allAuthorizedUnits = useSelector(state => state.auth.allAuthorizedUnits);

    const [selectedYear, setSelectedYear] = useState(
        isTableRow && selectedUnit && selectedUnit.year ? selectedUnit.year : ''
    );
    const [selectedPeriod, setSelectedPeriod] = useState('');
    const [previewFile, setPreviewFile] = useState(null);
    const [loadingFile, setLoadingFile] = useState(false);

    const [disableUnitYear, setDisableUnitYear] = useState(false);

    const { authUser } = useSelector(state => state.auth);
    const fontSettings = useSelector(state => state.meeting.fontSettings);
    const baseFontSize = fontSettings.baseFontSize;


    const currentYear = new Date().getFullYear();
    const potentialYears = [2023, 2024, currentYear];
    let availableYears = [];
    if (effectiveUnit) {
        if (isTableRow) {
            availableYears = [selectedYear];
        } else {
            availableYears = potentialYears.filter(year => {
                const recs = existingReports.filter(r => r.unitId === effectiveUnitId && r.year == year);
                const periods = new Set(recs.map(r => r.period));
                return periods.size < 2;
            });
        }
    } else {
        availableYears = potentialYears;
    }

    let availablePeriods = ["1.Dönem", "2.Dönem"];
    if (effectiveUnit && selectedYear) {
        const recs = fileList.filter(r => Number(r.unitId) === Number(effectiveUnitId) && r.year == selectedYear);
        availablePeriods = availablePeriods.filter(period => !recs.some(r => r.period === period));
    }

    useEffect(() => {
        if (!isTableRow) {
            setDisableUnitYear(false);
        }
    }, [effectiveUnit, selectedYear, isTableRow]);

    const uploadFile = async () => {
        if (previewFile && effectiveUnit && selectedYear && selectedPeriod) {
            setLoadingFile(true);
            const formData = new FormData();
            formData.append('file', previewFile);
            formData.append('userId', authUser.id);
            formData.append('unitId', effectiveUnitId);
            formData.append('inst', effectiveUnit.inst);
            formData.append('year', selectedYear);
            formData.append('period', selectedPeriod);

            const result = await postUploadPeriodicReportFileApi(formData);
            if (result.status) {
                const newFile = { ...result.data[0], user: authUser };
                setFileList(prev => [newFile, ...prev]);

                if (addAfterTable) {
                    newFile.user = authUser;
                    addAfterTable(newFile);
                }
            }
            setLoadingFile(false);
            setPreviewFile(null);
            if (!isTableRow) {
                // /// Buraları DeğişTirdim:
                // Sıfırdan ekleme modunda, birim ve yıl seçimi korunsun ve disable olsun.
                // Ayrıca, eklenen dönem dolayısıyla selectedPeriod sıfırlanır.
                setSelectedPeriod('');
                setDisableUnitYear(true);
            } else {
                setSelectedPeriod('');
            }
        } else {
            alert("Lütfen tüm alanları doldurun ve dosya seçin.");
        }
    };

    const handleDeleteFile = async (item) => {
        const result = await deleteObjectApi(PERIODIC_REPORT, item.id);
        if (result.status) {
            const result = await deletePeriodicReportFileApi(item.filePath);
            if (result.status) {
                const updatedFileList = fileList.filter(x => x.id !== item.id);
                setFileList(updatedFileList);

                const remainingReports = existingReports.filter(r => r.unitId === item.unitId && r.year === item.year);
                if (remainingReports.length === 0) {
                    setFileList(prev => prev.filter(r => !(r.unitId === item.unitId && r.year === item.year)));
                }

                if (updateAfterTable) {
                    updateAfterTable({ ...item, deleted: true });
                }
            }
        } else {
            swal_error("Dosya silinirken bir hata oluştu. Lütfen daha sonra tekrar deneyiniz.");
        }
    };

    useEffect(() => {
        if (!isTableRow && effectiveUnitId) {
            setFileList(existingReports.filter(r => Number(r.unitId) === Number(effectiveUnitId)));
        }
    }, [existingReports, effectiveUnitId, isTableRow]);

    const filteredUnitList = allAuthorizedUnits;

    return (
        <>
            <JumboCardQuick>
                <Grid container spacing={1}>
                    <Grid item xs={3}>
                        <Autocomplete
                            multiple={false}
                            id="autocomplete"
                            options={filteredUnitList}
                            size="small"
                            getOptionLabel={(option) => option.name}
                            value={
                                effectiveUnit
                                    ? isTableRow
                                        ? filteredUnitList.find(x => x.id === effectiveUnitId) || null
                                        : effectiveUnit
                                    : null
                            }
                            onChange={(event, newValue) => {
                                if (!isTableRow) {
                                    setLocalUnit(newValue);
                                    setSelectedYear('');
                                    setSelectedPeriod('');
                                    // /// Buraları DeğişTirdim: Birim değiştiğinde disable flag reset
                                    setDisableUnitYear(false);
                                }
                            }}
                            disabled={isTableRow || disableUnitYear}  // /// Buraları DeğişTirdim: disableUnitYear kontrolü eklendi
                            componentsProps={{
                                popper: {
                                    sx: {
                                        '& .MuiAutocomplete-listbox': {
                                            fontSize: baseFontSize,
                                        },
                                    },
                                },
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Kurul/Kol/Komisyon"
                                    placeholder="Kurul/Kol/Komisyon seçiniz"
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <FormControl fullWidth>
                            <InputLabel id="year" size="small">Yıl</InputLabel>
                            <Select
                                labelId="year"
                                label="Yıl"
                                size="small"
                                value={selectedYear}
                                onChange={(e) => {
                                    if (!isTableRow) {
                                        setSelectedYear(e.target.value);
                                        setSelectedPeriod('');
                                        // /// Buraları DeğişTirdim: Yıl değiştiğinde disable flag reset
                                        setDisableUnitYear(false);
                                    }
                                }}
                                disabled={isTableRow || disableUnitYear}  // /// Buraları DeğişTirdim: disableUnitYear kontrolü eklendi
                            >
                                {availableYears.map(year => (
                                    <MenuItem key={year} value={year}>{year}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                        <FormControl fullWidth>
                            <InputLabel id="period" size="small">Dönem</InputLabel>
                            <Select
                                labelId="period"
                                label="Dönem"
                                size="small"
                                value={selectedPeriod}
                                onChange={(e) => setSelectedPeriod(e.target.value)}
                            >
                                {availablePeriods.map(period => (
                                    <MenuItem key={period} value={period}>{period}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={1}>
                        <DzPreviews
                            title=""
                            size="medium"
                            uploadSize="single"
                            accept="application/pdf"
                            onChange={(data) => setPreviewFile(data)}
                            value={previewFile}
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <Button
                            variant="outlined"
                            size="medium"
                            color="success"
                            disabled={
                                !previewFile ||
                                !effectiveUnit ||
                                !selectedYear ||
                                !selectedPeriod ||
                                availablePeriods.length === 0
                            }
                            onClick={uploadFile}
                        >
                            {loadingFile ? 'Yükleniyor...' : 'Yükle'}
                        </Button>
                    </Grid>
                </Grid>
            </JumboCardQuick>

            <JumboCardQuick sx={{ marginTop: '10px' }}>
                {loadingFile ? <CircularProgress /> :

                    <PeriodicReportListTable
                        fileList={fileList}
                        handleDeleteItem={handleDeleteFile}
                    />
                }
            </JumboCardQuick>
        </>
    );
}

export default PeriodicReportFileCard;
